import "../index.css";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import ErrorBoundary from "@/shared/components/ui/Error/ErrorBoundary";
import Signup from "@/features/auth/components/Signup";
import ResetPassword from "@/features/auth/components/ResetPassword";
import MicrosoftCallbackHandler from "./components/MicrosoftCallbackHandler";
import GoogleCallbackHandler from "./components/GoogleCallbackHandler";
import AuthSuccessHandler from "./components/AuthSuccessHandler";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  showError,
  API_ERROR_TOAST_ID,
  getFriendlyApiErrorMessage,
  isCanceledRequest,
  markErrorToastShown,
  toastMutationReadError,
  toastQueryError,
} from "@/shared/utils/toast";
import { HeroUIProvider } from "@heroui/system";
import { TRIAL_USAGE_QUERY_KEY } from "@/shared/hooks/useTrialUsage";
import LayoutBg from "@/shared/components/layout/LayoutBg";
import AuthenticatedContent from "./AuthenticatedContent";
import { useState, useEffect } from "react";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";

import { ThemeProvider } from "@/shared/providers/ThemeProvider";

// Create a client for React Query
const queryClient = new QueryClient({
  // Reads are reported here once, after retries, instead of per failed attempt by axios.
  queryCache: new QueryCache({ onError: toastQueryError }),
  mutationCache: new MutationCache({
    onError: toastMutationReadError,
    // Anything a user adds or removes can move a trial counter (only fetched for trial accounts).
    onSettled: () => queryClient.invalidateQueries({ queryKey: TRIAL_USAGE_QUERY_KEY }),
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

// Make queryClient available globally for axios interceptor
if (typeof window !== "undefined") {
  window.queryClient = queryClient;
}

// Inner component to access useLocation hook
const AppRoutes = ({ hasToken }) => {
  const location = useLocation();

  // Check if current path is an auth callback route
  const isAuthCallback =
    location.pathname === "/auth-success" ||
    location.pathname === "/api/v1/auth/microsoft/callback" ||
    location.pathname === "/api/v1/auth/google/callback";

  if (location.pathname === "/reset-password") {
    return (
      <Routes>
        <Route element={<LayoutBg whiteBg={true} />}>
          <Route
            path="/reset-password"
            element={
              <ErrorBoundary>
                <ResetPassword />
              </ErrorBoundary>
            }
          />
        </Route>
      </Routes>
    );
  }

  // If it's an auth callback route
  if (isAuthCallback) {
    return (
      <Routes>
        <Route
          path="/auth-success"
          element={
            <ErrorBoundary>
              <AuthSuccessHandler />
            </ErrorBoundary>
          }
        />
        <Route
          path="/api/v1/auth/microsoft/callback"
          element={
            <ErrorBoundary>
              <MicrosoftCallbackHandler />
            </ErrorBoundary>
          }
        />
        <Route
          path="/api/v1/auth/google/callback"
          element={
            <ErrorBoundary>
              <GoogleCallbackHandler />
            </ErrorBoundary>
          }
        />
      </Routes>
    );
  }

  // Otherwise, show authenticated or unauthenticated routes
  if (hasToken || !!localStorage.getItem("token")) {
    return <AuthenticatedContent />;
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/signup" replace />} />
      <Route element={<LayoutBg whiteBg={true} />}>
        <Route
          path="/signup"
          element={
            <ErrorBoundary>
              <Signup />
            </ErrorBoundary>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/signup" replace />} />
    </Routes>
  );
};

const App = () => {
  const [hasToken, setHasToken] = useState(() => {
    // Initialize from localStorage
    return !!localStorage.getItem("token");
  });

  useEffect(() => {
    const checkToken = () => {
      const token = localStorage.getItem("token");
      setHasToken(!!token);
    };

    // Listen for storage changes
    const handleStorageChange = (e) => {
      if (e.key === "token" || e.key === null) {
        checkToken();
      }
    };

    // Listen for custom token update events
    const handleTokenUpdate = () => {
      checkToken();
    };

    // Listen for token expiration
    const handleTokenExpired = () => {
      setHasToken(false);
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("tokenUpdated", handleTokenUpdate);
    window.addEventListener("tokenExpired", handleTokenExpired);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("tokenUpdated", handleTokenUpdate);
      window.removeEventListener("tokenExpired", handleTokenExpired);
    };
  }, []);

  useEffect(() => {
    // Only an API call nobody handled is worth a toast here. Other uncaught errors
    // (ResizeObserver notices, browser extensions, third-party scripts, code bugs) mean
    // nothing to the user; render errors are caught by ErrorBoundary.
    const rejectionHandler = (event) => {
      const reason = event.reason;
      if (!reason?.isAxiosError) return;
      event.preventDefault();
      if (reason.__globalErrorToastShown || isCanceledRequest(reason)) return;
      markErrorToastShown(reason);
      showError(getFriendlyApiErrorMessage(reason), undefined, { toastId: API_ERROR_TOAST_ID });
    };

    const errorHandler = (event) => {
      console.error("[window error]", event.error ?? event.message);
    };

    window.addEventListener("unhandledrejection", rejectionHandler);
    window.addEventListener("error", errorHandler);
    return () => {
      window.removeEventListener("unhandledrejection", rejectionHandler);
      window.removeEventListener("error", errorHandler);
    };
  }, []);

  // Render the main application wrapped in providers
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <HeroUIProvider>
          <ErrorBoundary>
            <ToastContainer
              position="bottom-right"
              autoClose={20000}
              hideProgressBar={false}
              newestOnTop
              closeOnClick
              pauseOnHover
              draggable={false}
              toastClassName="!font-sans !text-sm !rounded-xl !border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 !shadow-[0_10px_40px_-12px_rgba(37,43,55,0.18)] !min-h-0 !z-[9999999999999999999]"
              bodyClassName="!text-gray-900 dark:!text-gray-100 !font-medium !text-[0.875rem] !p-0 !m-0 !z-[9999999999999999999]"
              limit={5}
            />
            <AppRoutes hasToken={hasToken} />
          </ErrorBoundary>
        </HeroUIProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

export default App;
